import { DateTime } from '@ksyos/date-time';
import { OrderedMap, Record } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import type { LabResult } from '../../../../../interfaces/labresult';
import { fetchJson } from '../../../../../../store/fetch';
import { ProfessionalSummaryDataAction } from '../../../../professionalSummaryData/ProfessionalSummaryDataSection';

const labResultRecordType = Record({
    id: null,
    codeCode: null,
    codeSystem: null,
    codeSystemName: null,
    codeDisplayName: null,
    codeSystemVersion: null,
    valueType: null,
    valueCode: null,
    valueCodeSystem: null,
    valueSystemVersion: null,
    valueDisplayName: null,
    valueValue: null,
    valueUnit: null,
    comment: null,
    startDate: null,
    endDate: null,
    action: ProfessionalSummaryDataAction.add,
});

export class LabResultRecord extends labResultRecordType {
    public readonly id: string;
    public readonly codeCode: string | null;
    public readonly codeSystem: string | null;
    public readonly codeSystemName: string | null;
    public readonly codeDisplayName: string | null;
    public readonly codeSystemVersion: string | null;
    public readonly valueType: string | null;
    public readonly valueCode: string | null;
    public readonly valueCodeSystem: string | null;
    public readonly valueSystemVersion: string | null;
    public readonly valueDisplayName: string | null;
    public readonly valueValue: string | null;
    public readonly valueUnit: string | null;
    public readonly comment: string | null;
    public readonly startDate: string | null;
    public readonly endDate: string | null;
    public readonly action: ProfessionalSummaryDataAction;

    constructor(values?: any) {
        super(values);
    }
}

export type LabResults = OrderedMap<string, LabResultRecord>;

export async function loadRequestLabResults(
    patient: { authorization?: { canSeePatientLabResults?: boolean }; id: string },
    requestId?: string
) {
    let labResults: LabResults = await loadPatientLabResult(patient, Boolean(requestId));

    if (requestId) {
        const requestLabResultsResponse = await fetchJson(`/api/requests/${requestId}/requestLabResults`, 'GET');
        for (const requestLabResult of requestLabResultsResponse.json.items) {
            const key = makeLabResultKey(requestLabResult);
            if (labResults.has(key)) {
                labResults = labResults.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
            } else {
                labResults = labResults.set(uuidv4(), new LabResultRecord(requestLabResult));
            }
        }
    }

    return labResults;
}

export async function loadProcessActivityLabResults(
    patient: { authorization?: { canSeePatientLabResults?: boolean }; id: string },
    processActivityId?: string
) {
    let labResults: LabResults = await loadPatientLabResult(patient);

    if (processActivityId) {
        const processActivityLabResultsResponse = await fetchJson(
            `/api/processActivities/${processActivityId}/labResults`,
            'GET'
        );
        for (const processActivityLabResult of processActivityLabResultsResponse.json.items) {
            const key = makeLabResultKey(processActivityLabResult);
            if (labResults.has(key)) {
                labResults = labResults.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
            } else {
                labResults = labResults.set(uuidv4(), new LabResultRecord(processActivityLabResult));
            }
        }
    }

    return labResults;
}

async function loadPatientLabResult(
    patient: { authorization?: { canSeePatientLabResults?: boolean }; id: string },
    isEditLabResults?: boolean
) {
    let labResults: LabResults = OrderedMap();
    if (patient.authorization && patient.authorization.canSeePatientLabResults) {
        const patientLabResultsResponse = await fetchJson(`/api/patients/${patient.id}/labResults`, 'GET');
        for (const patientLabResult of patientLabResultsResponse.json.items) {
            labResults = labResults.set(
                makeLabResultKey(patientLabResult),
                new LabResultRecord({ ...patientLabResult, action: ProfessionalSummaryDataAction.skip })
            );
        }
    }

    return labResults;
}

function makeLabResultKey(labResult: LabResult) {
    return (
        labResult.codeCode +
        '\u0000' +
        labResult.codeSystem +
        '\u0000' +
        labResult.codeSystemName +
        '\u0000' +
        labResult.codeDisplayName +
        '\u0000' +
        labResult.codeSystemVersion +
        '\u0000' +
        labResult.valueType +
        '\u0000' +
        labResult.valueCode +
        '\u0000' +
        labResult.valueCodeSystem +
        '\u0000' +
        labResult.valueSystemVersion +
        '\u0000' +
        labResult.valueDisplayName +
        '\u0000' +
        labResult.valueValue +
        '\u0000' +
        labResult.valueUnit +
        '\u0000' +
        labResult.comment +
        '\u0000' +
        labResult.startDate +
        '\u0000' +
        labResult.endDate
    );
}

export function filterAndPrepareLabResultsForSaving(labResults?: LabResults) {
    if (!labResults) {
        return [];
    }

    return labResults
        .valueSeq()
        .filter((labResult) => {
            if (!labResult) {
                return false;
            } else {
                return labResult.action === ProfessionalSummaryDataAction.pick;
            }
        })
        .toJS();
}

export function isCurrentLabResult(labResult: { endDate: string | null; startDate: string | null }) {
    if (labResult.startDate && DateTime.now().isBefore(labResult.startDate)) {
        return false;
    }

    if (labResult.endDate && DateTime.now().isAfter(labResult.endDate)) {
        return false;
    }

    return true;
}
